import CloseIcon from '@mui/icons-material/Close';
import { Box, Typography } from '@mui/material';
import { getDurationLabel, getTimeLabel } from '../../../helpers/time';

type FocusTimeDaySlotContentProps = Readonly<{
  slotId: string;
  slotTitle: string;
  startMinutes: number;
  endMinutes: number;
  onRemoveSlot: (slotId: string) => void;
}>;

const SLOT_CONTENT_SX = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  boxSizing: 'border-box',
  minHeight: 0,
  overflow: 'hidden',
  borderRadius: 'inherit',
} as const;

const SLOT_TITLE_ROW_SX = {
  display: 'flex',
  alignItems: 'baseline',
  minWidth: 0,
  overflow: 'hidden',
} as const;

const DELETE_ACTION_BUTTON_SX = {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  minWidth: 0,
  border: 0,
  backgroundColor: 'transparent',
  p: 0,
  overflow: 'hidden',
  color: 'var(--mui-palette-primary-main)',
  transition: 'color 120ms ease',
  cursor: 'pointer',
  '&:hover': {
    color: 'var(--mui-palette-primary-dark)',
    backgroundColor: 'transparent',
    svg: {
      fill: 'var(--mui-palette-error-main)',
    },
  },
  '&:focus-visible': {
    outline: '2px solid var(--mui-palette-primary-main)',
    outlineOffset: 2,
  },
} as const;

const DELETE_ICON_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  opacity: 0,
  visibility: 'hidden',
  transition: 'opacity 120ms ease',
} as const;

const getSlotDensity = (durationMinutes: number) => {
  if (durationMinutes <= 20) {
    return 'tiny';
  }

  if (durationMinutes <= 35) {
    return 'compact';
  }

  return 'regular';
};

const getSlotSizeStyles = (durationMinutes: number) => {
  const density = getSlotDensity(durationMinutes);

  if (density === 'tiny') {
    return {
      content: { gap: 0.5, px: 1, py: 0 },
      title: { lineHeight: '9px' },
      timeRange: { lineHeight: '9px' },
      duration: { lineHeight: '9px' },
      actions: { gap: 0.25 },
      deleteButton: { width: 10, height: 10 },
      deleteIconSize: 14,
    };
  }

  return {
    content: { gap: 0.75, px: 1, py: 0.5 },
    title: { lineHeight: '12px' },
    timeRange: { lineHeight: '12px' },
    duration: { lineHeight: '12px' },
    actions: { gap: 0.5 },
    deleteButton: { width: 16, height: 16 },
    deleteIconSize: 15,
  };
};

const getSlotTimeLabel = (minutes: number) => getTimeLabel(minutes).padStart(5, '0');

const FocusTimeDaySlotContent = ({
  slotId,
  slotTitle,
  startMinutes,
  endMinutes,
  onRemoveSlot,
}: FocusTimeDaySlotContentProps) => {
  const slotSizeStyles = getSlotSizeStyles(endMinutes - startMinutes);
  const timeRangeLabel = `${getSlotTimeLabel(startMinutes)}-${getSlotTimeLabel(endMinutes)}`;

  return (
    <Box sx={{ ...SLOT_CONTENT_SX, ...slotSizeStyles.content }}>
      <Box sx={SLOT_TITLE_ROW_SX}>
        <Typography
          variant="body2"
          noWrap
          sx={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            ...slotSizeStyles.title,
          }}
        >
          {slotTitle}
        </Typography>
        <Typography
          component="span"
          variant="body2"
          color="textSecondary"
          noWrap
          sx={{
            flexShrink: 0,
            ml: 0.5,
            ...slotSizeStyles.timeRange,
          }}
        >
          ({timeRangeLabel})
        </Typography>
      </Box>
      <Box
        component="button"
        type="button"
        aria-label={`Delete ${slotTitle}`}
        onClick={event => {
          event.stopPropagation();
          onRemoveSlot(slotId);
        }}
        sx={{ ...DELETE_ACTION_BUTTON_SX, ...slotSizeStyles.actions }}
      >
        <Box
          component="span"
          data-focus-time-delete-icon="true"
          aria-label={`Delete ${slotTitle}`}
          sx={{ ...DELETE_ICON_SX, ...slotSizeStyles.deleteButton }}
        >
          <CloseIcon sx={{ fontSize: slotSizeStyles.deleteIconSize }} />
        </Box>
        <Typography component="span" variant="body2" color="textSecondary" sx={{ ...slotSizeStyles.duration }}>
          {getDurationLabel(startMinutes, endMinutes)}
        </Typography>
      </Box>
    </Box>
  );
};

export default FocusTimeDaySlotContent;
